/**
 * System monitor tab (SUN-69) — admins only. Mockup: /home/agent/work/mockups/kiaan-system-monitor.html.
 * Dev box: numbers, 1-hour sparklines, what is using the box, task tags, rules, what happened.
 * Live box: READ ONLY (red frame). The only button is "Restart…", which asks Manak; it never restarts anything.
 * Refreshes every 30 s. Raw ids are never shown: only the labels the monitor sends.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSysEvents, getSysHistory, getSysRules, getSysState, getSysTasks, requestLiveRestart, setSysRules, setSysTargetTag, setSysTaskTag,
  sysErrorText, sysTargetAction, type SysBox, type SysEvent, type SysPoint, type SysRules, type SysState, type SysTarget, type SysTask, type SysTaskTag,
} from './kiaanApi';

export const LIVE_BANNER = 'Live box: the monitor only watches and tells you. Every action (restart, stop, clean-up) is a button that needs your yes. Nothing on live is paused by itself.';
export const NO_SWAP_TEXT = 'No swap set up. If memory runs out, the app is killed.';
const REFRESH_MS = 30000;

const TAGS: Record<string, { label: string; cls: string }> = {
  never: { label: 'never pause', cls: 'sm-t-crit' },
  pause_first: { label: 'pause first', cls: 'sm-t-p1' },
  pause_second: { label: 'pause second', cls: 'sm-t-p2' },
  ask_first: { label: 'ask first', cls: 'sm-t-ask' },
  live_watch_only: { label: 'watch only', cls: 'sm-t-ask' },
};
const CHANGEABLE = ['pause_first', 'pause_second', 'ask_first', 'never'] as const;

// The monitor sends memory, swap and disk in bytes.
const gb = (b: number | null | undefined) => (b == null ? '—' : `${(b / 1024 ** 3).toFixed(1)} GB`);
const mem = (b: number | null | undefined) => {
  if (b == null) return '—';
  const mb = b / 1024 ** 2;
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${Math.round(mb)} MB`;
};
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)}%`);
const clock = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
};
const barColor = (usedPct: number) => (usedPct >= 85 ? '#B91C1C' : usedPct >= 65 ? '#D97706' : '#166534');

function Spark({ values, color }: { values: Array<number | null>; color: string }) {
  const v = values.filter((x): x is number => x != null);
  if (v.length < 2) return <div className="sm-spark" aria-hidden="true" />;
  const lo = Math.min(...v), hi = Math.max(...v), span = hi - lo || 1;
  const pts = v.map((y, i) => `${((i / (v.length - 1)) * 120).toFixed(1)},${(26 - ((y - lo) / span) * 24).toFixed(1)}`).join(' ');
  return (
    <svg className="sm-spark" viewBox="0 0 120 28" preserveAspectRatio="none" aria-hidden="true" data-testid="sm-spark">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
    </svg>
  );
}

function Meter({ label, value, small, used, spark, sparkColor, foot }: {
  label: string; value: string; small?: string; used?: number | null; spark?: Array<number | null>; sparkColor?: string; foot?: string;
}) {
  return (
    <div className="sm-g">
      <span className="sm-l">{label}</span>
      <span className="sm-v">{value} {small && <small>{small}</small>}</span>
      {used != null && <div className="sm-bar"><i style={{ width: `${Math.max(0, Math.min(100, used))}%`, background: barColor(used) }} /></div>}
      {spark && <Spark values={spark} color={sparkColor ?? '#1E3A2B'} />}
      {foot && <span className="sm-foot">{foot}</span>}
    </div>
  );
}

function Meters({ s, points, live }: { s: SysState; points: SysPoint[]; live: boolean }) {
  const memUsed = s.mem && s.mem.total ? (1 - s.mem.available / s.mem.total) * 100 : null;
  const noSwap = !!s.swap && s.swap.total === 0;
  const swapUsed = s.swap && s.swap.total ? (1 - s.swap.free / s.swap.total) * 100 : null;
  const diskUsed = s.disk && s.disk.total ? (1 - s.disk.free / s.disk.total) * 100 : null;
  const spark = (k: keyof SysPoint) => points.map((p) => p[k] as number | null);
  return (
    <div className="sm-gauges">
      <Meter label={`Processor · ${s.cores} cores`} value={pct(s.cpu)} small={s.load ? `load ${s.load[0]}` : undefined} used={s.cpu} spark={live ? undefined : spark('cpu')} />
      <Meter label={`Memory · ${gb(s.mem?.total)}`} value={gb(s.mem?.available)} small="free" used={memUsed} spark={live ? undefined : spark('memAvail')} />
      {noSwap ? (
        <div className="sm-g">
          <span className="sm-l">Swap</span>
          <span className="sm-v sm-bad">none</span>
          <span className="sm-bad">{NO_SWAP_TEXT}</span>
        </div>
      ) : (
        <Meter label={`Swap · ${gb(s.swap?.total)}`} value={gb(s.swap?.free)} small="free" used={swapUsed} spark={live ? undefined : spark('swapFree')} sparkColor="#D97706" />
      )}
      <Meter
        label={`Disk · ${gb(s.disk?.total)}`} value={gb(s.disk?.free)} small="free" used={diskUsed}
        foot={s.site ? `${live ? 'aura.sunstellar.in' : 'dev site'} ${s.site.status ?? '—'}${s.site.seconds != null ? ` · ${s.site.seconds} s` : ''}` : undefined}
      />
    </div>
  );
}

const stateWords = (t: SysTarget) => {
  const st = (t.state ?? '').toLowerCase();
  const text = st.replace(/_/g, ' ') || '—';
  const cls = /pause|held|hold/.test(st) ? 'sm-s-pause' : /run|up|ok|working/.test(st) ? 'sm-s-run' : 'sm-s-idle';
  return { text: t.runs != null && t.maxRuns != null && /run|working/.test(st) ? `${text} · ${t.runs}/${t.maxRuns}` : text, cls, paused: /pause|held|hold/.test(st) };
};

type Act = (fn: () => Promise<unknown>, ok?: string) => Promise<void>;

function TargetsTable({ targets, live, act, onRestart }: { targets: SysTarget[]; live: boolean; act: Act; onRestart: () => void }) {
  return (
    <table className="sm-table">
      <thead>
        <tr><th>{live ? 'Process' : 'Process / agent'}</th>{!live && <th>Tag</th>}<th className="sm-n">Memory</th><th className="sm-n">CPU</th><th>State</th><th /></tr>
      </thead>
      <tbody>
        {targets.map((t) => {
          const st = stateWords(t);
          const locked = t.tag === 'never';
          const isApp = /aura/i.test(t.label) && !/redis|postgres|minio|chrome/i.test(t.label);
          const tag = TAGS[String(t.tag)] ?? null;
          return (
            <tr key={t.id}>
              <td>{t.label}{t.parts ? <span className="sm-foot"> · {t.parts} parts</span> : null}</td>
              {!live && (
                <td>
                  {locked || !tag ? (
                    <span className={`sm-tag ${tag?.cls ?? 'sm-t-ask'}`}>{tag?.label ?? '—'}</span>
                  ) : (
                    <select className="sm-tagsel" aria-label={`Tag for ${t.label}`} value={String(t.tag)}
                      onChange={(e) => act(() => setSysTargetTag(t.id, e.target.value as (typeof CHANGEABLE)[number]), 'Tag saved.')}>
                      {CHANGEABLE.map((k) => <option key={k} value={k}>{TAGS[k].label}</option>)}
                    </select>
                  )}
                </td>
              )}
              <td className="sm-n">{mem(t.mem)}</td>
              <td className="sm-n">{pct(t.cpu)}</td>
              <td>
                <span className={`sm-st ${st.cls}`}>{st.text}</span>
                {st.paused && t.pausedBy ? <span className="sm-foot"> · by {t.pausedBy}</span> : null}
              </td>
              <td>
                {live ? (
                  isApp ? <button type="button" className="sm-btn" onClick={onRestart}>Restart… needs your yes</button> : <span className="sm-btn sm-dis">—</span>
                ) : locked ? (
                  <span className="sm-btn sm-dis" aria-disabled="true">—</span>
                ) : (
                  <button type="button" className="sm-btn" onClick={() => act(() => sysTargetAction(t.id, st.paused ? 'resume' : 'pause'))}>
                    {st.paused ? 'Resume' : t.kind === 'job' || t.kind === 'lock' ? 'Hold' : 'Pause'}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const WHEN_SHORT: Record<SysTaskTag, string> = {
  priority: 'keeps running',
  'can-wait': 'paused first, resumed later',
  none: 'pauses after "pause first"',
};
const TASK_WORDS: Record<SysTaskTag, string> = { priority: 'priority', 'can-wait': 'can wait', none: 'normal' };

function TasksTable({ tasks, act }: { tasks: SysTask[]; act: Act }) {
  const order: Record<SysTaskTag, number> = { priority: 0, none: 1, 'can-wait': 2 };
  const rows = [...tasks].sort((a, b) => order[a.tag] - order[b.tag]).slice(0, 15);
  return (
    <>
      <h3 style={{ marginTop: 4 }}>Agent tasks by tag</h3>
      <table className="sm-table">
        <thead><tr><th>Task</th><th>Agent</th><th>Tag</th><th>When RAM is short</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="sm-foot">No open tasks.</td></tr>}
          {rows.map((t) => (
            <tr key={t.id}>
              <td>{t.identifier ? `${t.identifier} ` : ''}{t.title}</td>
              <td>{t.agent ?? '—'}</td>
              <td>
                <select className="sm-tagsel" aria-label={`Tag for ${t.identifier ?? t.title}`} value={t.tag}
                  onChange={(e) => act(() => setSysTaskTag(t.id, e.target.value as SysTaskTag), 'Task tag saved.')}>
                  {(['priority', 'none', 'can-wait'] as SysTaskTag[]).map((k) => <option key={k} value={k}>{TASK_WORDS[k]}</option>)}
                </select>
              </td>
              <td>{WHEN_SHORT[t.tag]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Switch({ on, label, onToggle, disabled }: { on: boolean; label: string; onToggle: () => void; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} className={`sm-sw${on ? ' sm-on' : ''}`} onClick={onToggle} />;
}

export function SystemMonitor() {
  const [box, setBox] = useState<SysBox>('dev');
  const [state, setState] = useState<{ state: SysState | null; liveLastSeen: string | null } | null>(null);
  const [points, setPoints] = useState<SysPoint[]>([]);
  const [events, setEvents] = useState<SysEvent[]>([]);
  const [rules, setRules] = useState<SysRules | null>(null);
  const [tasks, setTasks] = useState<SysTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const boxRef = useRef(box);
  boxRef.current = box;
  const live = box === 'live';

  const load = useCallback(async () => {
    const b = boxRef.current;
    const [st, hi, ev, ru, ta] = await Promise.allSettled([
      getSysState(b), getSysHistory(b), getSysEvents(b), b === 'dev' ? getSysRules() : Promise.resolve(null), b === 'dev' ? getSysTasks() : Promise.resolve([] as SysTask[]),
    ]);
    if (boxRef.current !== b) return; // the admin switched box while this was loading
    if (st.status === 'rejected') { setError(sysErrorText(st.reason)); return; }
    setError(null);
    setState(st.value);
    setPoints(hi.status === 'fulfilled' ? hi.value : []);
    setEvents(ev.status === 'fulfilled' ? ev.value : []);
    setRules(ru.status === 'fulfilled' ? ru.value : null);
    setTasks(ta.status === 'fulfilled' ? ta.value ?? [] : []);
  }, []);

  useEffect(() => {
    setState(null);
    setPoints([]);
    setEvents([]);
    setNote(null);
    void load();
    const t = window.setInterval(() => { void load(); }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [box, load]);

  const act: Act = async (fn, ok) => {
    setNote(null);
    try { await fn(); if (ok) setNote(ok); await load(); } catch (e) { setNote(sysErrorText(e)); }
  };
  const restart = () => act(async () => {
    const r = await requestLiveRestart();
    setNote(`Asked Manak${r.identifier ? ` (${r.identifier})` : ''}. Nothing was restarted.`);
  });

  const s = state?.state ?? null;
  return (
    <div className={`sm-root${live ? ' sm-live' : ''}`} data-testid="system-monitor" data-box={box}>
      <div className="sm-boxes">
        <button type="button" className={`sm-pill${!live ? ' sm-on' : ''}`} aria-pressed={!live} onClick={() => setBox('dev')}>● Dev box .88 · agents</button>
        <button type="button" className={`sm-pill${live ? ' sm-on' : ''}`} aria-pressed={live} onClick={() => setBox('live')}>● Live box .83 · AURA</button>
        <span className="sm-auto">
          {live ? <b>Automation <span className="sm-foot">watch + tell only</span></b> : (
            <>
              <b>Automation</b>
              <Switch on={!!s?.automation} label="Automation" disabled={!rules} onToggle={() => act(() => setSysRules({ automation: !s?.automation }))} />
              {s?.automation ? 'on' : 'off'}
            </>
          )}
        </span>
      </div>
      <div className="sm-body">
        {error && <div className="kw-banner" role="status">{error}</div>}
        {note && <div className="kw-banner" role="status">{note}</div>}
        {live && <div className="sm-banner">{LIVE_BANNER}</div>}
        {!s && !error && <div className="kw-small" style={{ padding: 12 }}>{state && live ? 'Waiting for the live box to send its numbers…' : 'Loading…'}</div>}
        {s && (
          <>
            <Meters s={s} points={points} live={live} />
            <div className={live ? undefined : 'sm-row2'}>
              <div className="sm-card">
                <h3>What is using the box</h3>
                <TargetsTable targets={s.targets ?? []} live={live} act={act} onRestart={restart} />
                {!live && <TasksTable tasks={tasks} act={act} />}
              </div>
              {!live && (
                <div className="sm-card">
                  <h3>Automation rules</h3>
                  {(rules?.rules ?? []).map((r) => (
                    <div className="sm-rule" key={r.id}>
                      <span>{r.text}</span>
                      <Switch on={r.on} label={r.text} onToggle={() => act(() => setSysRules({ rules: { [r.id]: !r.on } }))} />
                    </div>
                  ))}
                  <div className="sm-foot">"Never pause" items are never touched. "Ask first" items wait for your tap.</div>
                  <h3>What happened</h3>
                  <div className="sm-feed">
                    {events.length === 0 && <div className="sm-foot">Nothing yet.</div>}
                    {events.map((e, i) => (
                      <div key={`${e.at}-${i}`} className={e.level === 'alert' || e.level === 'error' ? 'sm-bad' : undefined}>
                        <time>{clock(e.at)}</time><span>{e.text}{e.by ? ` · ${e.by}` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {live && state?.liveLastSeen && <div className="sm-foot">Last numbers from the live box: {clock(state.liveLastSeen)}</div>}
          </>
        )}
      </div>
    </div>
  );
}
